import { BookOpen, Clock } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';

import { Badge, Card, CardContent, Skeleton } from '@workspace/shadcn';
import { EmptySection, useData } from '@workspace/ui';

import { courseImage, formatDuration, formatLevel } from '../lib/format.ts';

const WWW_URL = import.meta.env.VITE_WWW_APP_URL ?? '#';

export default function MyCourses() {
  const [searchParams] = useSearchParams();
  const { data, isLoading, isError } = useData('GET /me/courses', {
    pageSize: 100,
  });

  const enrollments = data?.records ?? [];
  // The payment callback lands here after Moyasar sends the browser back.
  const purchase = searchParams.get('purchase');

  return (
    <div className="mx-auto w-full max-w-6xl">
      <h1 className="text-2xl font-bold">My Courses</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Everything you are enrolled in.
      </p>

      {purchase === 'success' && (
        <p className="border-primary/30 bg-primary/5 text-primary mt-4 rounded-md border px-4 py-3 text-sm">
          Payment received. Your course is ready below.
        </p>
      )}

      {purchase === 'failed' && (
        <p className="text-destructive border-destructive/30 bg-destructive/5 mt-4 rounded-md border px-4 py-3 text-sm">
          We could not confirm that payment. If you were charged, it will appear
          here shortly.
        </p>
      )}

      {isError ? (
        <p className="text-muted-foreground mt-10">
          We could not load your courses. Please refresh the page.
        </p>
      ) : isLoading ? (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : enrollments.length === 0 ? (
        <div className="mt-10">
          <EmptySection
            title="No courses yet"
            subtitle="You have not enrolled in anything"
            description="Browse the catalogue and enrol to see your courses here."
          />
          <div className="mt-4 text-center">
            <a
              href={`${WWW_URL}/courses`}
              className="text-primary text-sm font-medium"
            >
              Browse courses
            </a>
          </div>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {enrollments.map((enrollment) => {
            const course = enrollment.course;
            const duration = formatDuration(course.durationHours);
            return (
              <Link key={enrollment.id} to={`/courses/${course.slug}`}>
                <Card className="group h-full overflow-hidden pt-0 transition-shadow hover:shadow-lg">
                  <div className="aspect-16/10 w-full overflow-hidden">
                    <img
                      src={courseImage(course.imageUrl)}
                      alt={course.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="space-y-3">
                    {course.category && (
                      <Badge variant="secondary">{course.category}</Badge>
                    )}
                    <h2 className="font-semibold">{course.title}</h2>
                    <p className="text-muted-foreground line-clamp-2 text-sm">
                      {course.oneLiner}
                    </p>
                    <div className="text-muted-foreground flex items-center justify-between text-sm">
                      {/* The empty span keeps the level end-anchored when the duration is hidden. */}
                      <span className="flex items-center gap-1">
                        {duration && (
                          <>
                            <Clock className="size-4" />
                            {duration}
                          </>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="size-4" />
                        {formatLevel(course.level)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
